/**
 * Copies text to the clipboard (Prompt 138), preferring the modern async
 * Clipboard API but always falling back to a hidden-textarea +
 * document.execCommand("copy") when that API is unavailable or rejects.
 * navigator.clipboard.writeText can be silently blocked on an insecure
 * (plain HTTP) origin - exactly the situation the live HTTP test site hits
 * - so this never assumes the modern API works and never fails silently:
 * the caller always gets back a real boolean it must act on.
 */
export async function copyTextToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Insecure origin, missing permission, or an unsupported browser -
      // fall through to the legacy fallback below rather than giving up.
    }
  }
  return copyViaExecCommand(text);
}

function copyViaExecCommand(text) {
  if (typeof document === "undefined" || typeof document.execCommand !== "function") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  // Off-screen but still focusable/selectable - execCommand("copy") requires
  // a real text selection, which an invisible (display:none) element can
  // never have.
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);

  let succeeded = false;
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    succeeded = document.execCommand("copy");
  } catch {
    succeeded = false;
  } finally {
    document.body.removeChild(textarea);
  }

  return succeeded;
}
